import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { useScenarioListener } from '../../sockets/scenarios.util';
import { ClassConversionTask } from '../../types/scenario-modeling.types';
import { useAppQuery } from '../use-app-query.hook';

export const useScenarioClassConversionTasks = ({
  scenarioId,
  numberOfFetchedItems = 100,
  currentPage = 1,
}: {
  currentPage?: number;
  numberOfFetchedItems?: number;
  scenarioId: string;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const { data, isLoading, isRefetching } = useAppQuery<
    { data: ClassConversionTask[]; totalPages: number },
    HttpException
  >({
    queryKey: [
      QueryKey.GET_SCENARIO_CLASS_CONVERSION_TASKS,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.SCENARIOS, routePath: [scenarioId, 'class-conversion-tasks'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
          },
        },
      ),
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SCENARIO_CLASS_CONVERSION_TASKS] });
  }, [queryClient]);

  useScenarioListener('conversions-updated', () =>
    queryClient.refetchQueries({
      queryKey: [QueryKey.GET_SCENARIO_CLASS_CONVERSION_TASKS],
    }),
  );

  return {
    invalidateQuery,
    scenarioConversionTasks: data?.data || [],
    totalPages: data?.totalPages || 1,
    isLoading: isLoading || isRefetching,
  };
};
